import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function AdminLogin() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem("masons_token", data.token);
            navigate("/admin/dashboard");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#060709] flex items-center justify-center px-6" data-testid="admin-login-page">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="w-full max-w-md rounded-2xl border border-[#22262F] bg-[#121418] p-10"
            >
                <div className="flex items-center gap-3 mb-2">
                    <Logo />
                    <span className="font-display font-extrabold uppercase text-white text-lg">Staff Portal</span>
                </div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-8">
                    Masons Automotive — Dispatch Console
                </p>
                <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Staff email"
                        data-testid="admin-email-input"
                        className="w-full bg-[#0B0C0E] border border-[#383E4C] rounded-xl px-4 py-3.5 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-amber-500 transition-colors duration-200"
                    />
                    <input
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Password"
                        data-testid="admin-password-input"
                        className="w-full bg-[#0B0C0E] border border-[#383E4C] rounded-xl px-4 py-3.5 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-amber-500 transition-colors duration-200"
                    />
                    {error && (
                        <div className="text-sm text-red-400 font-mono" data-testid="admin-login-error">
                            {error}
                        </div>
                    )}
                    <motion.button
                        type="submit"
                        disabled={loading}
                        data-testid="admin-login-submit-button"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full bg-amber-500 disabled:opacity-50 text-[#0B0C0E] font-bold uppercase tracking-widest text-sm rounded-xl px-8 py-4 hover:bg-amber-400 transition-colors duration-200 inline-flex items-center justify-center gap-2"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                        {loading ? "Verifying..." : "Access Console"}
                    </motion.button>
                </form>
                <a href="/" data-testid="admin-back-to-site-link" className="block mt-6 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 hover:text-amber-400 transition-colors duration-200">
                    ← Back to site
                </a>
            </motion.div>
        </div>
    );
}
